import { useState, useEffect, useCallback, useId } from "react";
import { useTranslation } from "react-i18next";
import { getApiErrorPayload, paymentMethodsAPI } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { PaymentMethodIcon } from "@/components/PaymentMethodIcon";
import { CreditCard } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { Loading } from "@/components/ui/loader";

interface BranchPaymentMethod {
	id: number;
	code: string;
	description?: string | null;
	iconUrl?: string | null;
	branchActive: boolean;
}

/** Toggle which (company) payment methods are available at this branch. */
export function PaymentMethodBranchToggles({ branchId }: { branchId: number }) {
	const { t } = useTranslation();
	const { toast } = useToast();
	const fieldId = useId();
	const [rows, setRows] = useState<BranchPaymentMethod[]>([]);
	const [loading, setLoading] = useState(true);
	const [savingId, setSavingId] = useState<number | null>(null);

	const showError = useCallback(
		(error: unknown) => {
			const payload = getApiErrorPayload(error);
			toast({
				title: t("common.error"),
				description: t(payload.key, (payload.params ?? {}) as Record<string, string>),
				variant: "destructive",
			});
		},
		[t, toast],
	);

	const fetchRows = useCallback(() => {
		setLoading(true);
		paymentMethodsAPI
			.getBranchAvailability(branchId)
			.then((data: BranchPaymentMethod[]) => setRows(data))
			.catch(showError)
			.finally(() => setLoading(false));
	}, [branchId, showError]);

	useEffect(() => {
		fetchRows();
	}, [fetchRows]);

	const toggle = async (row: BranchPaymentMethod, next: boolean) => {
		setSavingId(row.id);
		setRows(prev => prev.map(r => (r.id === row.id ? { ...r, branchActive: next } : r)));
		try {
			await paymentMethodsAPI.setBranchAvailability(branchId, row.id, next);
		} catch (error) {
			showError(error);
			setRows(prev => prev.map(r => (r.id === row.id ? { ...r, branchActive: !next } : r)));
		} finally {
			setSavingId(null);
		}
	};

	if (loading) return <Loading />;
	if (rows.length === 0) {
		return (
			<p className="text-muted-foreground py-6 text-center text-sm">{t("orderChannelsAdmin.payments.empty")}</p>
		);
	}

	return (
		<div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4" data-testid="branch-payment-methods">
			{rows.map(row => {
				const name = row.description || row.code;
				const saving = savingId === row.id;
				return (
					<label
						key={row.id}
						htmlFor={`${fieldId}-${row.id}`}
						aria-busy={saving}
						className={cn(
							"flex min-w-0 cursor-pointer flex-col gap-3 rounded-xl border p-3 transition-colors focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ring-offset-background",
							row.branchActive
								? "border-primary/50 bg-primary/5"
								: "border-border bg-card hover:border-primary/40",
							saving && "cursor-wait opacity-60",
						)}
					>
						<div className="flex items-center justify-between gap-3">
							{row.iconUrl ? (
								<PaymentMethodIcon
									iconUrl={row.iconUrl}
									alt=""
									className="h-9 w-9 shrink-0 object-contain"
								/>
							) : (
								<span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
									<CreditCard className="h-5 w-5" aria-hidden />
								</span>
							)}
							<Checkbox
								id={`${fieldId}-${row.id}`}
								className="h-5 w-5"
								checked={row.branchActive}
								disabled={saving}
								onCheckedChange={next => toggle(row, next === true)}
								aria-label={name}
							/>
						</div>
						<span className="break-words text-sm font-medium leading-relaxed">{name}</span>
					</label>
				);
			})}
		</div>
	);
}
